import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, MessageSquareReply, PenSquare, PhoneCall } from "lucide-react";
import { useMe } from "../App";
import { api, useApi } from "../lib/api";
import { Badge, Button, Card, cx, Empty, ErrorNote, fmtDate, fmtDay, inputCls, Loading, Modal, PageHeader, Stat } from "../components/ui";

type Section = "replies" | "reviews" | "tasks";

const ALERT_LABEL: Record<string, string> = {
  reply: "Replies",
  opt_out: "Opt-outs",
  bounce: "Bounces",
  account_pause: "Paused accounts",
  new_opp: "New opportunities",
  mailbox_pause: "Mailbox paused",
  signal_stale: "Stale signals",
  low_confidence: "Drafts that need attention",
  queue_limit: "Account queue full",
  info: "Notices",
};

export default function Today() {
  const { me } = useMe();
  const [all, setAll] = useState(false);
  const { data, error, loading, reload } = useApi<any>(`/today${all ? "?all=1" : ""}`, [all]);
  const [task, setTask] = useState<any>(null);
  const [focus, setFocus] = useState<Section | null>(null);

  const alerts: any[] = data?.alerts ?? [];
  const replies = alerts.filter((a) => a.kind === "reply" || a.kind === "opt_out");
  const others = alerts.filter((a) => a.kind !== "reply" && a.kind !== "opt_out");
  const reviews: any[] = data?.reviews ?? [];
  const tasks: any[] = data?.tasks ?? [];
  const show = (s: Section) => !focus || focus === s;
  const exceptions = reviews.reduce((n, r) => n + r.exceptions, 0);
  const overdue = tasks.filter((t) => t.overdue).length;

  const closeAlert = async (a: any) => {
    await api("/alerts/close", { body: { createdAt: a.createdAt, id: a.id, ownerSub: a.ownerSub } });
    reload();
  };
  const closeAll = async (list: any[]) => {
    for (const a of list) await api("/alerts/close", { body: { createdAt: a.createdAt, id: a.id, ownerSub: a.ownerSub } });
    reload();
  };

  if (loading && !data) return <Loading rows={8} />;
  return (
    <div className="space-y-6">
      <PageHeader
        title="Today"
        sub={new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
        actions={
          <>
            {me.isAdmin && (
              <label className="flex items-center gap-2 text-sm text-ink-2">
                <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> All reps
              </label>
            )}
            <Link to="/campaigns/new">
              <Button tone="primary">New campaign</Button>
            </Link>
          </>
        }
      />
      <ErrorNote error={error} />

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Replies" value={replies.length} sub={replies.length ? "answer these first" : "nothing new"} onClick={() => setFocus(focus === "replies" ? null : "replies")} active={focus === "replies"} />
        <Stat label="Drafts to review" value={reviews.length} sub={exceptions ? `${exceptions} contacts need a look` : "campaign sequences"} onClick={() => setFocus(focus === "reviews" ? null : "reviews")} active={focus === "reviews"} />
        <Stat label="Tasks due" value={tasks.length} sub={overdue ? `${overdue} overdue` : "calls and LinkedIn"} tone={overdue ? "warn" : undefined} onClick={() => setFocus(focus === "tasks" ? null : "tasks")} active={focus === "tasks"} />
      </div>

      {show("replies") && (
        <Card title={<span className="flex items-center gap-2"><MessageSquareReply size={16} /> Replies</span>} actions={replies.length > 1 && <Button tone="ghost" onClick={() => closeAll(replies)}>Mark all handled</Button>}>
          {!replies.length ? (
            <Empty>No new replies. When a prospect answers, their sequence stops and the reply shows up here.</Empty>
          ) : (
            <ul className="divide-y divide-line">
              {replies.map((a) => (
                <li key={a.id} className="flex flex-wrap items-start justify-between gap-2 py-3">
                  <div className="min-w-0">
                    <div className="text-sm text-ink">{a.message}</div>
                    <div className="mt-0.5 text-xs text-muted">
                      {fmtDate(a.createdAt)}
                      {a.campaignId && a.contactId && (
                        <>
                          {" · "}
                          <Link className="text-accent-text hover:underline" to={`/campaigns/${a.campaignId}/contacts/${a.contactId}`}>
                            Open
                          </Link>
                        </>
                      )}
                    </div>
                  </div>
                  <Button tone="ghost" onClick={() => closeAlert(a)}>
                    Handled
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {show("reviews") && (
        <Card title={<span className="flex items-center gap-2"><PenSquare size={16} /> Drafts to review</span>}>
          {!reviews.length ? (
            <Empty>Nothing waiting. New campaigns appear here once Claude has drafted them.</Empty>
          ) : (
            <ul className="divide-y divide-line">
              {reviews.map((r) => (
                <li key={r.campaignId} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-ink">{r.name}</div>
                    <div className="text-xs text-muted">
                      {r.offering} · {r.draftStatus === "approved" ? `${r.exceptions} contact(s) need a look` : r.draftStatus === "failed" ? "drafting failed" : `${r.pending} contacts waiting for approval`}
                      {r.source === "template" && " · template draft, redraft needed"}
                    </div>
                  </div>
                  <Link to={r.draftStatus === "approved" ? `/campaigns/${r.campaignId}/review` : `/campaigns/${r.campaignId}`}>
                    <Button tone="primary">{r.draftStatus === "approved" ? "Review contacts" : "Review campaign"}</Button>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {show("tasks") && (
        <Card title={<span className="flex items-center gap-2"><PhoneCall size={16} /> Tasks due</span>}>
          {!tasks.length ? (
            <Empty>No calls or LinkedIn steps due. Emails keep sending on schedule.</Empty>
          ) : (
            <ul className="divide-y divide-line">
              {groupByContact(tasks).map(([name, ts]) => (
                <li key={name} className="py-3">
                  <div className="text-sm font-medium text-ink">
                    {name} <span className="font-normal text-muted">· {ts[0].accountName}</span>
                  </div>
                  {ts.map((t: any) => (
                    <div key={t.n} className="mt-1.5 flex flex-wrap items-center justify-between gap-2 text-sm">
                      <div className="flex items-center gap-2 text-ink-2">
                        <span>{t.label}</span>
                        <span className="text-xs text-muted">due {fmtDay(t.dueDate)}</span>
                        {t.overdue && <Badge tone="warn">Overdue</Badge>}
                      </div>
                      <Button onClick={() => setTask(t)}>Open</Button>
                    </div>
                  ))}
                </li>
              ))}
            </ul>
          )}
          {(data?.upcoming ?? []).length > 0 && <div className="mt-3 border-t border-line pt-3 text-xs text-muted">Coming up: {data.upcoming.slice(0, 5).map((t: any) => `${t.label} · ${t.contactName} (${fmtDay(t.dueDate)})`).join(" · ")}</div>}
        </Card>
      )}

      {!focus && others.length > 0 && <Notices alerts={others} onClose={closeAlert} onCloseAll={closeAll} />}
      {task && <TaskModal task={task} onClose={() => setTask(null)} onDone={() => { setTask(null); reload(); }} />}
    </div>
  );
}

/** Other alerts, collapsed to one line per kind (Phase 5.1). */
function Notices({ alerts, onClose, onCloseAll }: { alerts: any[]; onClose: (a: any) => void; onCloseAll: (list: any[]) => void }) {
  const groups = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const a of alerts) m.set(a.kind, [...(m.get(a.kind) ?? []), a]);
    return [...m.entries()];
  }, [alerts]);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Card title="Notices" pad={false}>
      <ul className="divide-y divide-line">
        {groups.map(([kind, list]) => (
          <li key={kind} className="px-5 py-3">
            <div className="flex items-center justify-between gap-2">
              <button className="flex min-w-0 items-center gap-1.5 text-sm text-ink" onClick={() => setOpen(open === kind ? null : kind)} aria-expanded={open === kind}>
                {open === kind ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                <span className="font-medium">{ALERT_LABEL[kind] ?? kind}</span>
                <span className="text-muted">({list.length})</span>
                {list.length === 1 && <span className="ml-1 truncate text-muted">· {list[0].message.slice(0, 90)}</span>}
              </button>
              <Button tone="ghost" onClick={() => onCloseAll(list)}>
                Dismiss{list.length > 1 ? " all" : ""}
              </Button>
            </div>
            {open === kind && (
              <ul className="mt-2 space-y-2 pl-6">
                {list.map((a) => (
                  <li key={a.id} className="flex items-start justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <div className={cx(kind === "mailbox_pause" || kind === "bounce" ? "text-bad" : "text-ink-2")}>{a.message}</div>
                      <div className="text-xs text-muted">
                        {fmtDate(a.createdAt)}
                        {a.campaignId && (
                          <>
                            {" · "}
                            <Link className="text-accent-text hover:underline" to={a.contactId ? `/campaigns/${a.campaignId}/contacts/${a.contactId}` : `/campaigns/${a.campaignId}`}>
                              Open
                            </Link>
                          </>
                        )}
                      </div>
                    </div>
                    <Button tone="ghost" onClick={() => onClose(a)}>
                      Dismiss
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function groupByContact(tasks: any[]): [string, any[]][] {
  const m = new Map<string, any[]>();
  for (const t of tasks) m.set(t.contactName, [...(m.get(t.contactName) ?? []), t]);
  return [...m.entries()];
}

function TaskModal({ task, onClose, onDone }: { task: any; onClose: () => void; onDone: () => void }) {
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const done = async () => {
    try {
      await api(`/tasks/${task.campaignId}/${task.contactId}/${task.n}/done`, { body: { note } });
      onDone();
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  return (
    <Modal
      title={`${task.label}: ${task.contactName}`}
      onClose={onClose}
      actions={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button tone="primary" onClick={done}>
            Mark done
          </Button>
        </>
      }
    >
      <div className="mail rounded-lg bg-surface-2 p-3">{task.content || "(no script)"}</div>
      <textarea className={inputCls} rows={2} placeholder="Note (optional, logged to Salesforce)" value={note} onChange={(e) => setNote(e.target.value)} />
      <ErrorNote error={err} />
    </Modal>
  );
}
